/**
 * Standard CompanyData shape consumed by CompanyFundamentals.js
 *
 * Both transformAlphaVantage() and transformFmp() return this same schema,
 * so the React component never needs to know which source was used.
 *
 * Fields that a source doesn't supply are set to null (never undefined),
 * allowing the component to use simple `?? '—'` fallbacks everywhere.
 *
 * @typedef {Object} CompanyData
 * @property {string}  symbol
 * @property {string}  companyName
 * @property {string}  exchange
 * @property {string}  currency
 * @property {string}  sector
 * @property {string}  industry
 * @property {string}  country
 * @property {string}  description
 * @property {string}  website
 * @property {string}  ipoDate
 * @property {string}  employees
 * @property {number}  sharesOutstanding
 * @property {number}  mktCap
 * @property {number}  price           — null for AV (use live quote)
 * @property {number}  beta
 * @property {number}  dividendYield   — decimal (0.015 = 1.5%)
 * @property {number}  week52Low
 * @property {number}  week52High
 * @property {number}  pe
 * @property {number}  fwdPe           — null for FMP free tier
 * @property {number}  pb
 * @property {number}  evEbitda
 * @property {number}  ps
 * @property {number}  epsTTM
 * @property {number}  revenueTTM
 * @property {number}  grossMarginTTM  — decimal (0.43 = 43%)
 * @property {number}  opMarginTTM
 * @property {number}  netMarginTTM
 * @property {number}  revenueGrowthYoY
 * @property {number}  earningsGrowthYoY
 * @property {number}  percentInsiders
 * @property {number}  percentInstitutions
 * @property {number}  roe
 * @property {number}  roa
 * @property {Array<{date:string, eps:number|null, estimatedEps:number|null, surprise:number|null, surprisePercentage:number|null, reportedDate:string|null, reportTime:string|null, revenue:number|null}>} quartersHistory
 * @property {Array<{year:string, eps:number|null, revenue:number|null}>} yearsHistory
 */

function n(val) {
  const v = parseFloat(val);
  return isNaN(v) ? null : v;
}

// ─── Alpha Vantage transformer ────────────────────────────────────────────────
// Sources: OVERVIEW endpoint + EARNINGS endpoint

export function transformAlphaVantage(overview, earnings) {
  if (!overview) return null;

  const revenueTTM = n(overview.RevenueTTM);
  const grossProfitTTM = n(overview.GrossProfitTTM);
  const grossMarginTTM = revenueTTM > 0 && grossProfitTTM != null
    ? grossProfitTTM / revenueTTM
    : null;

  const quartersHistory = (earnings?.quarterlyEarnings || [])
    .slice(0, 8)
    .reverse()
    .map((q) => ({
      date: q.fiscalDateEnding?.slice(0, 7) ?? '',
      eps: n(q.reportedEPS),
      estimatedEps: n(q.estimatedEPS),
      surprise: n(q.surprise),
      surprisePercentage: n(q.surprisePercentage),
      reportedDate: q.reportedDate || null,
      reportTime: q.reportTime || null,
      revenue: null, // AV EARNINGS doesn't include revenue per quarter
    }));

  const yearsHistory = (earnings?.annualEarnings || [])
    .slice(0, 8)
    .reverse()
    .map((a) => ({
      year: a.fiscalDateEnding?.slice(0, 4) ?? '',
      eps: n(a.reportedEPS),
      revenue: null,
    }));

  return {
    // Identity
    symbol: overview.Symbol ?? null,
    companyName: overview.Name ?? null,
    exchange: overview.Exchange ?? null,
    currency: overview.Currency ?? null,
    sector: overview.Sector ?? null,
    industry: overview.Industry ?? null,
    country: overview.Country ?? null,
    description: overview.Description ?? null,
    website: overview.OfficialSite ?? null,
    ipoDate: null,
    employees: null,
    sharesOutstanding: n(overview.SharesOutstanding),
    // Market
    mktCap: n(overview.MarketCapitalization),
    price: null, // AV OVERVIEW has no current price; use live quote from StreamsContext
    beta: n(overview.Beta),
    dividendYield: n(overview.DividendYield),
    week52Low: n(overview['52WeekLow']),
    week52High: n(overview['52WeekHigh']),
    // Valuation
    pe: n(overview.TrailingPE),
    fwdPe: n(overview.ForwardPE),
    pb: n(overview.PriceToBookRatio),
    evEbitda: n(overview.EVToEBITDA),
    ps: n(overview.PriceToSalesRatioTTM),
    // Income (TTM)
    epsTTM: n(overview.DilutedEPSTTM),
    revenueTTM,
    grossMarginTTM,
    opMarginTTM: n(overview.OperatingMarginTTM),
    netMarginTTM: n(overview.ProfitMargin),
    revenueGrowthYoY: n(overview.QuarterlyRevenueGrowthYOY),
    earningsGrowthYoY: n(overview.QuarterlyEarningsGrowthYOY),
    percentInsiders: n(overview.PercentInsiders),
    percentInstitutions: n(overview.PercentInstitutions),
    // Profitability
    roe: n(overview.ReturnOnEquityTTM),
    roa: n(overview.ReturnOnAssetsTTM),
    // Chart history
    quartersHistory,
    yearsHistory,
  };
}

// ─── FMP transformer ──────────────────────────────────────────────────────────
// Sources: stable/profile + stable/income-statement?period=quarter&limit=12
//          + stable/key-metrics-ttm
//
// NOTE: As of 2026, income-statement and key-metrics-ttm require a paid FMP plan.
//       This transformer is kept ready so upgrading is a one-line swap in the hook.

export function transformFmp(profile, income, keyMetrics) {
  if (!profile) return null;

  const incomeData = income || [];
  const last4 = incomeData.slice(0, 4);
  const revenueTTM = last4.reduce((s, q) => s + (q.revenue || 0), 0);
  const grossProfitTTM = last4.reduce((s, q) => s + (q.grossProfit || 0), 0);
  const netIncomeTTM = last4.reduce((s, q) => s + (q.netIncome || 0), 0);
  const epsTTM = last4.reduce((s, q) => s + (q.epsdiluted || 0), 0);
  const grossMarginTTM = revenueTTM > 0 ? grossProfitTTM / revenueTTM : null;
  const netMarginTTM = revenueTTM > 0 ? netIncomeTTM / revenueTTM : null;
  const opMarginTTM = last4[0]?.operatingIncomeRatio ?? null;

  const prior4 = incomeData.slice(4, 8);
  const revenuePrior = prior4.reduce((s, q) => s + (q.revenue || 0), 0);
  const revenueGrowthYoY = revenuePrior > 0 ? (revenueTTM - revenuePrior) / revenuePrior : null;

  const [rangeLoStr, rangeHiStr] = (profile.range || '').split('-');
  const week52Low = parseFloat(rangeLoStr) || null;
  const week52High = parseFloat(rangeHiStr) || null;

  const quartersHistory = incomeData
    .slice(0, 8)
    .reverse()
    .map((q) => ({
      date: q.date?.slice(0, 7) ?? '',
      eps: q.epsdiluted ?? null,
      estimatedEps: null,
      surprise: null,
      surprisePercentage: null,
      reportedDate: q.date ?? null,
      reportTime: null,
      revenue: q.revenue ?? null,
    }));

  const byYear = {};
  incomeData.forEach((q) => {
    const year = q.calendarYear || q.date?.slice(0, 4);
    if (!year) return;
    if (!byYear[year]) byYear[year] = { eps: 0, revenue: 0 };
    byYear[year].eps += q.epsdiluted || 0;
    byYear[year].revenue += q.revenue || 0;
  });
  const yearsHistory = Object.entries(byYear)
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-8)
    .map(([year, v]) => ({ year, eps: v.eps, revenue: v.revenue }));

  const km = keyMetrics || {};
  return {
    // Identity
    symbol: profile.symbol ?? null,
    companyName: profile.companyName ?? null,
    exchange: profile.exchangeShortName ?? null,
    currency: profile.currency ?? null,
    sector: profile.sector ?? null,
    industry: profile.industry ?? null,
    country: profile.country ?? null,
    description: profile.description ?? null,
    website: profile.website ?? null,
    ipoDate: profile.ipoDate ?? null,
    employees: profile.fullTimeEmployees ?? null,
    sharesOutstanding: profile.sharesOutstanding ?? null,
    // Market
    mktCap: profile.mktCap ?? null,
    price: profile.price ?? null,
    beta: profile.beta ?? null,
    dividendYield: profile.dividendYield ?? null,
    week52Low,
    week52High,
    // Valuation
    pe: profile.pe ?? null,
    fwdPe: null,
    pb: km.priceToBookRatioTTM ?? null,
    evEbitda: km.enterpriseValueOverEBITDATTM ?? null,
    ps: km.priceToSalesRatioTTM ?? null,
    // Income (TTM)
    epsTTM,
    revenueTTM,
    grossMarginTTM,
    opMarginTTM,
    netMarginTTM,
    revenueGrowthYoY,
    earningsGrowthYoY: null,
    percentInsiders: null,
    percentInstitutions: null,
    // Profitability
    roe: km.returnOnEquityTTM ?? null,
    roa: km.returnOnAssetsTTM ?? null,
    // Chart history
    quartersHistory,
    yearsHistory,
  };
}
